import { useMemo, useState, type ReactNode } from "react";
import { Loader2, Copy } from "lucide-react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { MerchantSelector } from "@/presentation/merchants/components/MerchantSelector";
import { ToolSheetShell } from "./ToolSheetShell";

const client = generateClient<Schema>();

interface FormState {
  businessId: string;
  firstName: string;
  lastName: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  ssnLast4: string;
}

const INITIAL: FormState = {
  businessId: "",
  firstName: "",
  lastName: "",
  dobYear: "",
  dobMonth: "",
  dobDay: "",
  ssnLast4: "",
};

type ForceResult = NonNullable<
  Awaited<ReturnType<typeof client.mutations.forceVerifiedIdentity>>["data"]
>;

interface RunState {
  running: boolean;
  result?: ForceResult;
  error?: string;
}

interface ForceVerifiedIdentitySheetProps {
  onClose: () => void;
}

export function ForceVerifiedIdentitySheet({
  onClose,
}: ForceVerifiedIdentitySheetProps) {
  const isProd = import.meta.env.VITE_HUB_ENVIRONMENT === "production";

  const [form, setForm] = useState<FormState>(INITIAL);
  const [run, setRun] = useState<RunState>({ running: false });

  const formInvalid = useMemo(() => {
    if (!form.businessId.trim()) return true;
    if (!form.firstName.trim() || !form.lastName.trim()) return true;
    if (!/^\d{4}$/.test(form.dobYear)) return true;
    if (!/^\d{1,2}$/.test(form.dobMonth)) return true;
    if (!/^\d{1,2}$/.test(form.dobDay)) return true;
    const y = Number(form.dobYear);
    const m = Number(form.dobMonth);
    const d = Number(form.dobDay);
    if (y < 1900 || y > 2100) return true;
    if (m < 1 || m > 12 || d < 1 || d > 31) return true;
    if (!/^\d{4}$/.test(form.ssnLast4)) return true;
    return false;
  }, [form]);

  function onlyDigits(s: string): string {
    return s.replace(/\D/g, "");
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function pad(n: string): string {
    const v = Number(n);
    return v < 10 ? `0${v}` : String(v);
  }

  async function handleSubmit() {
    setRun({ running: true });
    try {
      const dob = `${form.dobYear}-${pad(form.dobMonth)}-${pad(form.dobDay)}`;
      const result = await client.mutations.forceVerifiedIdentity({
        input: {
          businessId: form.businessId.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dob,
          ssnLast4: form.ssnLast4.trim(),
        },
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      setRun({
        running: false,
        result: result.data ?? undefined,
      });
      toast.success("Identity forced");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRun({ running: false, error: message });
      toast.error(`Force failed: ${message}`);
    }
  }

  function copyHash() {
    if (!run.result?.identityHash) return;
    void navigator.clipboard.writeText(run.result.identityHash);
    toast.success("Hash copied");
  }

  return (
    <ToolSheetShell
      title="Force Verified Identity"
      onClose={onClose}
      footer={
        <Button
          onClick={handleSubmit}
          disabled={isProd || run.running || formInvalid}
          className="w-full"
        >
          {run.running ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Forcing…
            </>
          ) : (
            "Force Verified Identity"
          )}
        </Button>
      }
    >
      {isProd && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          This tool is disabled in production.
        </div>
      )}
      <p className="text-muted-foreground text-sm">
        Drives the production <code>upsertVerifiedIdentity</code> +{" "}
        <code>recordIdentityCondition</code> path with a stub Stripe client. Two
        runs with the same PII produce the same identity hash — use to verify
        the dedupe + referral-ineligible flow.
      </p>

      <div className="mt-6 space-y-4">
        <Field label="Business">
          <MerchantSelector
            value={form.businessId}
            onChange={(id) => update("businessId", id)}
            disabled={isProd}
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="First name">
            <Input
              value={form.firstName}
              onChange={(e) => update("firstName", e.target.value)}
              disabled={isProd}
            />
          </Field>
          <Field label="Last name">
            <Input
              value={form.lastName}
              onChange={(e) => update("lastName", e.target.value)}
              disabled={isProd}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="DOB Year">
            <Input
              inputMode="numeric"
              maxLength={4}
              value={form.dobYear}
              placeholder="1990"
              onChange={(e) => update("dobYear", onlyDigits(e.target.value))}
              disabled={isProd}
            />
          </Field>
          <Field label="Month">
            <Input
              inputMode="numeric"
              maxLength={2}
              value={form.dobMonth}
              placeholder="1"
              onChange={(e) => update("dobMonth", onlyDigits(e.target.value))}
              disabled={isProd}
            />
          </Field>
          <Field label="Day">
            <Input
              inputMode="numeric"
              maxLength={2}
              value={form.dobDay}
              placeholder="1"
              onChange={(e) => update("dobDay", onlyDigits(e.target.value))}
              disabled={isProd}
            />
          </Field>
        </div>

        <Field
          label="SSN last 4"
          hint="4 digits. Combined with name + DOB to compute the identity hash."
        >
          <Input
            className="font-mono"
            maxLength={4}
            value={form.ssnLast4}
            inputMode="numeric"
            onChange={(e) => update("ssnLast4", onlyDigits(e.target.value))}
            disabled={isProd}
          />
        </Field>
      </div>

      {(run.result || run.error) && (
        <div className="border-border mt-6 space-y-3 border-t pt-4">
          {run.error && (
            <pre className="overflow-x-auto rounded bg-red-50 p-3 text-xs text-red-700">
              {run.error}
            </pre>
          )}
          {run.result && <ResultPanel result={run.result} onCopy={copyHash} />}
        </div>
      )}
    </ToolSheetShell>
  );
}

interface ResultPanelProps {
  result: ForceResult;
  onCopy: () => void;
}

function ResultPanel({ result, onCopy }: ResultPanelProps) {
  const referralBad =
    result.referralTrackStage === "ineligible" ||
    result.identityTrackStage === "duplicate";
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="text-muted-foreground mb-1 text-xs font-medium">
          Identity hash
        </div>
        <div className="flex items-center gap-2">
          <code className="bg-muted flex-1 truncate rounded px-2 py-1 font-mono text-xs">
            {result.identityHash}
          </code>
          <Button variant="outline" size="sm" onClick={onCopy}>
            <Copy className="h-3 w-3" />
          </Button>
        </div>
        {result.isFirstVerification ? (
          <div className="mt-1 text-xs text-emerald-600">
            First verification for this hash.
          </div>
        ) : (
          <div className="mt-1 text-xs text-amber-600">
            Hash already existed — collision recorded.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge
          label="Identity"
          value={result.identityTrackStage}
          tone={
            result.identityTrackStage === "duplicate" ? "danger" : "neutral"
          }
        />
        <Badge
          label="Referral"
          value={result.referralTrackStage}
          tone={referralBad ? "danger" : "neutral"}
        />
      </div>

      {result.featureFlagBypassed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          <code>release-referral-program</code> was off for this user — gate
          bypassed by the tool.
        </div>
      )}

      {result.collisionGroup.length > 0 && (
        <div>
          <div className="text-muted-foreground mb-1 text-xs font-medium">
            Collision group
          </div>
          <div className="flex flex-wrap gap-1">
            {result.collisionGroup.map((id) => (
              <code
                key={id}
                className="bg-muted rounded px-2 py-0.5 font-mono text-xs"
              >
                {id}
              </code>
            ))}
          </div>
        </div>
      )}

      {result.conflictingBusinessIds &&
        result.conflictingBusinessIds.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-red-700">
              Conflicting businesses
            </div>
            <div className="flex flex-wrap gap-1">
              {result.conflictingBusinessIds.map((id) => (
                <code
                  key={id}
                  className="rounded bg-red-50 px-2 py-0.5 font-mono text-xs text-red-700"
                >
                  {id}
                </code>
              ))}
            </div>
          </div>
        )}

      {result.ineligibilityReason && (
        <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          Ineligibility reason: <code>{result.ineligibilityReason}</code>
        </div>
      )}
    </div>
  );
}

interface BadgeProps {
  label: string;
  value: string;
  tone: "neutral" | "danger";
}

function Badge({ label, value, tone }: BadgeProps) {
  const palette =
    tone === "danger"
      ? "border-red-300 bg-red-50 text-red-700"
      : "border-border bg-muted text-foreground";
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${palette}`}
    >
      <span className="font-medium">{label}:</span>
      <code className="font-mono">{value}</code>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="block">
      <span className="text-muted-foreground mb-1 block text-xs font-medium">
        {label}
      </span>
      {children}
      {hint && (
        <span className="text-subtle-foreground mt-1 block text-xs">
          {hint}
        </span>
      )}
    </label>
  );
}
